import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Chip,
    IconButton,
    Box,
    Typography,
    Paper,
    Divider,
    Grid,
    Tooltip,
} from '@mui/material';
import {
    AttachFile,
    Close,
    Send,
    Add,
    Refresh,
} from '@mui/icons-material';
import RichTextEditor from 'react-rte';
import { showNotification } from '../../../common/Notification/ToastifyNotification';

const EmailDialog = ({
    open,
    onClose,
    onSend,
    to = '',
    cc = [],
    subject = '',
    body = '',
    defaultBody = '',
    title = "Compose Email",
    sendButtonText = "Send",
    actionName,
    paData,
    rows
}) => {
    // Form state
    const [formData, setFormData] = useState({
        to: '',
        cc: [],
        subject: '',
        currentCc: '',
    });

    const [editorValue, setEditorValue] = useState(RichTextEditor.createEmptyValue());
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);

    // Initialize form with props
    useEffect(() => {
        if (open) {
            setFormData({
                to: to || '',
                cc: Array.isArray(cc) ? cc : (cc ? [cc] : []),
                subject: subject || '',
                currentCc: '',
            });

            if (body) {
                try {
                    setEditorValue(RichTextEditor.createValueFromString(body, 'html'));
                } catch (error) {
                    setEditorValue(RichTextEditor.createEmptyValue());
                }
            } else {
                setEditorValue(RichTextEditor.createEmptyValue());
            }

            setAttachments([]);
        }
    }, [open, to, cc, subject, body]);

    // Handle input changes
    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    // Add CC recipient - FIXED: This now adds to existing CC, doesn't replace
    const handleAddCc = () => {
        if (formData.currentCc && isValidEmail(formData.currentCc)) {
            setFormData(prev => ({
                ...prev,
                cc: [...prev.cc, prev.currentCc.trim()], // This adds to existing CC array
                currentCc: ''
            }));
        }
    };

    // Remove CC recipient - FIXED: This only removes the specific CC email
    const handleRemoveCc = (index) => {
        setFormData(prev => ({
            ...prev,
            cc: prev.cc.filter((_, i) => i !== index) // This only removes one email
        }));
    };

    // Email validation
    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    // Handle file attachment
    const handleFileAttach = (event) => {
        const files = Array.from(event.target.files);
        if (files.length <= 2) {
            const newAttachments = files.map(file => ({
                file,
                name: file.name,
                size: file.size,
                id: Math.random().toString(36).substr(2, 9)
            }));

            setAttachments(prev => [...prev, ...newAttachments]);
            event.target.value = '';
        } else {
            showNotification(`You can't add more than 2 attachments`, 'error');
        }
    };

    // Remove attachment
    const handleRemoveAttachment = (id) => {
        setAttachments(prev => prev.filter(att => att.id !== id));
    };

    // Trigger file input
    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    // Handle send email
    const handleSend = () => {
        if (!formData.to || !isValidEmail(formData.to)) {
            return;
        }

        if (!formData.subject) {
            showNotification('Please provide subject before send email', 'error');
            return;
        }

        const htmlContent = editorValue.toString('html');
        const plainText = htmlContent.toString('plainText').trim();

        // Remove all HTML tags and check if content is empty
        const textWithoutTags = htmlContent.replace(/<[^>]*>/g, '').trim();

        // Common empty HTML structures
        const isEmptyHtml = [
            '<p><br></p>',
            '<p></p>',
            '<p><br/></p>',
            '<div><br></div>',
            '<div></div>',
            '<p>&nbsp;</p>',
            '<div>&nbsp;</div>'
        ].includes(htmlContent);

        if (isEmptyHtml || textWithoutTags === '' || plainText === '') {
            showNotification(`Without email body you can't send email`, 'error');
            return;
        }

        const emailData = {
            to: formData.to,
            cc: formData.cc, // This includes both initial CC and newly added CC
            subject: formData.subject,
            body: htmlContent,
            attachments: attachments
        };

        onSend?.(emailData);
        handleClose();
    };

    // Reset form and close
    const handleClose = () => {
        onClose?.();
    };

    // Handle Enter key in CC field
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddCc();
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleChange = (text) => {
        setEditorValue(text);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{title}</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    {/* To Field */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="To"
                            value={formData.to}
                            onChange={handleInputChange('to')}
                            disabled
                            placeholder="recipient@example.com"
                            required
                            error={formData.to !== '' && !isValidEmail(formData.to)}
                            helperText={formData.to !== '' && !isValidEmail(formData.to) ? 'Please enter a valid email address' : ''}
                            size="small"
                        />
                    </Grid>

                    {/* CC Field */}
                    <Grid item xs={12}>
                        <Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth
                                    label="CC"
                                    value={formData.currentCc}
                                    onChange={handleInputChange('currentCc')}
                                    onKeyPress={handleKeyPress}
                                    placeholder="cc@example.com"
                                    error={formData.currentCc !== '' && !isValidEmail(formData.currentCc)}
                                    helperText={formData.currentCc !== '' && !isValidEmail(formData.currentCc) ? 'Please enter a valid email address' : ''}
                                    size="small"
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleAddCc}
                                    disabled={!formData.currentCc || !isValidEmail(formData.currentCc)}
                                    startIcon={<Add />}
                                    size="small"
                                    style={{
                                        height: '40px'
                                    }}
                                >
                                    Add
                                </Button>
                            </Box>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {formData.cc.map((email, index) => (
                                    <Chip
                                        key={index}
                                        label={email}
                                        onDelete={index === 0 ? undefined : () => handleRemoveCc(index)}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    {/* Subject Field */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Subject"
                            value={formData.subject}
                            onChange={handleInputChange('subject')}
                            placeholder="Email subject"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                            <Tooltip title="Reset Email" onClick={() => setEditorValue(RichTextEditor.createValueFromString(defaultBody, 'html'))}>
                                <Refresh color='primary' style={{ cursor: 'pointer' }} />
                            </Tooltip>
                        </Box>
                        <RichTextEditor
                            value={editorValue}
                            placeholder="Write an Email..."
                            onChange={handleChange}
                            editorStyle={{
                                minHeight: '200px',
                                fontSize: '14px',
                            }}
                        />
                        {actionName === 'bidding' && (
                            <Box sx={{ p: 1, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', mt: 1 }}>
                                <Typography variant="body2" color="#856404">
                                    ⚠️ <strong>Required:</strong> Please include "Technical Specifications Form" in the email body before sending.
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            onChange={handleFileAttach}
                            style={{ display: 'none' }}
                        />
                        <Button
                            startIcon={<AttachFile />}
                            onClick={handleAttachClick}
                            variant="outlined"
                            size="small"
                        >
                            Attach Files
                        </Button>

                        {attachments.length > 0 && (
                            <Box mt={1}>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Attachments:
                                </Typography>
                                {attachments.map((attachment) => (
                                    <Paper
                                        key={attachment.id}
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            mt: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                            <AttachFile sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                                {attachment.name}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ mx: 1 }}>
                                            {formatFileSize(attachment.size)}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveAttachment(attachment.id)}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Paper>
                                ))}
                            </Box>
                        )}
                        {paData.coverpage && (
                            <Box mt={1}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1,
                                        mt: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                        <AttachFile sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                            {paData.coverpage.fileName}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ mx: 1 }}>
                                        {formatFileSize(paData.coverpage.size)}
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                        {paData.rfp && (
                            <Box mt={1}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1,
                                        mt: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                        <AttachFile sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                            {paData.rfp.fileName}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ mx: 1 }}>
                                        {formatFileSize(paData.rfp.size)}
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                        {(actionName === 'purchase_action' && rows.length > 0 && rows.find((a) => a.type === 'Approved Purchase Order')?.sortUrl) && (
                            <Box mt={1}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1,
                                        mt: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                                        <AttachFile sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                            {rows.find((a) => a.type === 'Approved Purchase Order')?.resource}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ mx: 1 }}>
                                        {rows.find((a) => a.type === 'Approved Purchase Order')?.size}
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color="inherit" variant='outlined'>
                    Cancel
                </Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    color="primary"
                    startIcon={<Send />}
                    disabled={!formData.to || !isValidEmail(formData.to)}
                >
                    {sendButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmailDialog;